// Stick Stories — Shopify Storefront API helper
// Used client-side for customer auth, orders, and progress saving.
// Requires window.SS_SHOPIFY.storefrontToken to be set via Liquid.

const SS_CUSTOMER_TOKEN_KEY = 'ss_customer_token';
const SS_CUSTOMER_EMAIL_KEY = 'ss_customer_email';
const SS_CUSTOMER_NAME_KEY  = 'ss_customer_name';

function ssStorefrontToken() {
  return window.SS_SHOPIFY && window.SS_SHOPIFY.storefrontToken;
}

function ssStorefrontGql(query, variables) {
  const token = ssStorefrontToken();
  if (!token) return Promise.reject(new Error('Storefront token manquant'));
  return fetch('/api/2024-01/graphql.json', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': token
    },
    body: JSON.stringify({ query: query, variables: variables || {} })
  }).then(function(r) { return r.json(); });
}

// ── Auth helpers ─────────────────────────────────────────────────────────────

function ssGetCustomerToken() {
  try { return localStorage.getItem(SS_CUSTOMER_TOKEN_KEY) || null; } catch(e) { return null; }
}

function ssGetCustomerEmail() {
  try { return localStorage.getItem(SS_CUSTOMER_EMAIL_KEY) || null; } catch(e) { return null; }
}

function ssGetCustomerName() {
  try { return localStorage.getItem(SS_CUSTOMER_NAME_KEY) || null; } catch(e) { return null; }
}

function ssSaveCustomerSession(token, email, name) {
  try {
    localStorage.setItem(SS_CUSTOMER_TOKEN_KEY, token);
    localStorage.setItem(SS_CUSTOMER_EMAIL_KEY, email || '');
    localStorage.setItem(SS_CUSTOMER_NAME_KEY, name || '');
    window.dispatchEvent(new Event('ss-auth-changed'));
  } catch(e) {}
}

function ssClearCustomerSession() {
  try {
    localStorage.removeItem(SS_CUSTOMER_TOKEN_KEY);
    localStorage.removeItem(SS_CUSTOMER_EMAIL_KEY);
    localStorage.removeItem(SS_CUSTOMER_NAME_KEY);
    window.dispatchEvent(new Event('ss-auth-changed'));
  } catch(e) {}
}

// ── Login ────────────────────────────────────────────────────────────────────

function ssCustomerLogin(email, password) {
  return ssStorefrontGql(
    'mutation Login($input: CustomerAccessTokenCreateInput!) {' +
    '  customerAccessTokenCreate(input: $input) {' +
    '    customerAccessToken { accessToken expiresAt }' +
    '    customerUserErrors { code message }' +
    '  }' +
    '}',
    { input: { email: email, password: password } }
  ).then(function(res) {
    var result = res.data && res.data.customerAccessTokenCreate;
    if (!result) throw new Error('Erreur de connexion.');
    if (result.customerUserErrors && result.customerUserErrors.length > 0) {
      var msg = result.customerUserErrors[0].message;
      if (msg.toLowerCase().includes('unidentified customer')) msg = 'Email ou mot de passe incorrect.';
      throw new Error(msg);
    }
    var token = result.customerAccessToken.accessToken;
    // Fetch customer name
    return ssStorefrontGql(
      'query Me($token: String!) { customer(customerAccessToken: $token) { firstName lastName email } }',
      { token: token }
    ).then(function(me) {
      var c = me.data && me.data.customer;
      var name = c ? ((c.firstName || '') + ' ' + (c.lastName || '')).trim() : email;
      ssSaveCustomerSession(token, email, name);
      return { token: token, email: email, name: name };
    });
  });
}

// ── Register ─────────────────────────────────────────────────────────────────

function ssCustomerRegister(firstName, lastName, email, password) {
  return ssStorefrontGql(
    'mutation Register($input: CustomerCreateInput!) {' +
    '  customerCreate(input: $input) {' +
    '    customer { id email firstName }' +
    '    customerUserErrors { code message }' +
    '  }' +
    '}',
    { input: { firstName: firstName, lastName: lastName, email: email, password: password, acceptsMarketing: false } }
  ).then(function(res) {
    var result = res.data && res.data.customerCreate;
    if (!result) throw new Error('Erreur lors de la création du compte.');
    if (result.customerUserErrors && result.customerUserErrors.length > 0) {
      var msg = result.customerUserErrors[0].message;
      if (msg.toLowerCase().includes('already taken')) msg = 'Un compte existe déjà avec cet email. Connectez-vous.';
      if (msg.toLowerCase().includes('password')) msg = 'Le mot de passe doit faire au moins 5 caractères.';
      throw new Error(msg);
    }
    // Auto-login after registration
    return ssCustomerLogin(email, password);
  });
}

// ── Logout ───────────────────────────────────────────────────────────────────

function ssCustomerLogout() {
  var token = ssGetCustomerToken();
  if (token) {
    ssStorefrontGql(
      'mutation Logout($token: String!) { customerAccessTokenDelete(customerAccessToken: $token) { deletedAccessToken } }',
      { token: token }
    ).catch(function() {});
  }
  ssClearCustomerSession();
}

// ── Orders ───────────────────────────────────────────────────────────────────

function ssCustomerGetOrders() {
  var token = ssGetCustomerToken();
  if (!token) return Promise.reject(new Error('Non connecté'));
  return ssStorefrontGql(
    'query Orders($token: String!) {' +
    '  customer(customerAccessToken: $token) {' +
    '    orders(first: 20, sortKey: PROCESSED_AT, reverse: true) {' +
    '      edges { node {' +
    '        name processedAt fulfillmentStatus financialStatus' +
    '        currentTotalPrice { amount currencyCode }' +
    '        lineItems(first: 3) { edges { node { title quantity } } }' +
    '        successfulFulfillments(first: 1) {' +
    '          trackingCompany' +
    '          trackingInfo(first: 1) { number url }' +
    '        }' +
    '        statusUrl' +
    '      } }' +
    '    }' +
    '  }' +
    '}',
    { token: token }
  ).then(function(res) {
    if (!res.data || !res.data.customer) throw new Error('Session expirée. Reconnectez-vous.');
    return res.data.customer.orders.edges.map(function(e) { return e.node; });
  });
}

// ── Book progress (per-customer localStorage) ─────────────────────────────────

function ssProgressKey() {
  var token = ssGetCustomerToken();
  return 'ss_book_progress_' + (token ? token.slice(-16) : 'guest');
}

function ssLoadProgress() {
  try { return JSON.parse(localStorage.getItem(ssProgressKey()) || 'null'); } catch(e) { return null; }
}

function ssSaveProgress(state) {
  try { localStorage.setItem(ssProgressKey(), JSON.stringify(state)); } catch(e) {}
}

// ── Expose globals ────────────────────────────────────────────────────────────
Object.assign(window, {
  ssGetCustomerToken, ssGetCustomerEmail, ssGetCustomerName,
  ssCustomerLogin, ssCustomerRegister, ssCustomerLogout,
  ssCustomerGetOrders,
  ssLoadProgress, ssSaveProgress,
  ssClearCustomerSession
});
